import { RefObject, useEffect, useState } from 'react';

interface IUseOnFocusAnyProps {
  ref: RefObject<HTMLElement | null> | HTMLElement | null;
  onFocus?: (event: FocusEvent) => void;
  onBlur?: (event: FocusEvent) => void;
  ignoreClasses?: string[];
}

const useOnFocusAny = ({ ref, onFocus, onBlur, ignoreClasses = [] }: IUseOnFocusAnyProps): boolean => {
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    const el = ref instanceof HTMLElement ? ref : ref?.current;
    if (!el) return;

    const handleFocus = (event: FocusEvent) => {
      if (
        el.contains(document.activeElement) ||
        el === document.activeElement ||
        el.contains(event.target as Node) ||
        el === event.target
      ) {
        onFocus?.(event);
        setIsFocused(true);
      } else {
        if (ignoreClasses.length > 0 && event.target instanceof Element) {
          const closestElement = ignoreClasses.some((className) => (event.target as Element).closest(className));
          if (closestElement) return; // Ignore focus if it matches any ignore class
        }
        setIsFocused(false);
        onBlur?.(event);
      }
    };

    document.addEventListener('focus', handleFocus, true);

    return () => {
      document.removeEventListener('focus', handleFocus, true);
    };
  }, [onBlur, onFocus, ref, ignoreClasses]);

  return isFocused;
};

export default useOnFocusAny;
